import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import MainCard from 'components/MainCard';
import useGet from 'hooks/useGet';

import {
  formatCurrency,
  formatDateTime,
  getCollection,
  normalizeOrder,
  toTitleCase
} from './helpers';

const paymentStatuses = [
  '',
  'created',
  'pending',
  'authorized',
  'captured',
  'failed',
  'cancelled',
  'refunded'
];

const statusColorMap = {
  created: 'default',
  pending: 'warning',
  authorized: 'info',
  captured: 'success',
  failed: 'error',
  cancelled: 'default',
  refunded: 'secondary'
};

export default function CustomProductPayments() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [paymentStatus, setPaymentStatus] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const endpoint = useMemo(() => {
    const params = new URLSearchParams();
    params.set('page', String(page + 1));
    params.set('limit', String(rowsPerPage));
    if (search.trim()) params.set('q', search.trim());
    if (paymentStatus) params.set('paymentStatus', paymentStatus);
    return `/api/admin/custom-product-payments/orders?${params.toString()}`;
  }, [page, paymentStatus, rowsPerPage, search]);

  const { data, error, loading } = useGet(endpoint);
  const orders = getCollection(data, ['orders']).map(normalizeOrder);

  return (
    <Stack spacing={2.5}>
      <Stack spacing={0.5}>
        <Typography variant="h4">Custom Product Payments</Typography>
        <Typography color="text.secondary">
          Review custom product orders and update payment status.
        </Typography>
      </Stack>

      <MainCard>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <TextField
            fullWidth
            size="small"
            label="Search by order, customer, or product"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(0);
            }}
          />
          <TextField
            select
            size="small"
            label="Payment Status"
            value={paymentStatus}
            onChange={(event) => {
              setPaymentStatus(event.target.value);
              setPage(0);
            }}
            sx={{ minWidth: 200 }}
          >
            {paymentStatuses.map((status) => (
              <MenuItem key={status || 'all'} value={status}>
                {status ? toTitleCase(status) : 'All Statuses'}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </MainCard>

      {error ? <Alert severity="error">Failed to load custom product payments.</Alert> : null}

      <MainCard title={`Orders (${Number(data?.total || orders.length)})`}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Order ID</TableCell>
                <TableCell>Product</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Payment Status</TableCell>
                <TableCell>Currency</TableCell>
                <TableCell>Total</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    Loading custom product payments...
                  </TableCell>
                </TableRow>
              ) : orders.length ? (
                orders.map((order) => (
                  <TableRow key={order.id} hover>
                    <TableCell>{order.id}</TableCell>
                    <TableCell>{order.customProduct?.title || '-'}</TableCell>
                    <TableCell>{order.customerEmail || order.customerName || '-'}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={toTitleCase(order.paymentStatus)}
                        color={statusColorMap[String(order.paymentStatus).toLowerCase()] || 'default'}
                      />
                    </TableCell>
                    <TableCell>{order.currency}</TableCell>
                    <TableCell>{formatCurrency(order.currency, order.totalAmount)}</TableCell>
                    <TableCell>{formatDateTime(order.createdAt)}</TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => navigate(`/custom-products/payments/${order.id}`)}
                      >
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    No custom product payments found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={Number(data?.total || 0)}
          page={page}
          onPageChange={(_, nextPage) => setPage(nextPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(Number(event.target.value));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 20, 50, 100]}
        />
      </MainCard>
    </Stack>
  );
}
